import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import { format, parseISO } from 'date-fns';
import { fr as frLocale } from 'date-fns/locale';
import { BookingStatusChip } from './BookingStatusChip';
import type { Booking } from '../../shared/services/bookings.service';

function formatPrice(cents: number | null, currency: string | null) {
  if (cents == null || !currency) return null;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

type Action = {
  key: string;
  label: string;
  onClick: () => void;
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'success' | 'error' | 'inherit';
  disabled?: boolean;
};

type Props = {
  booking: Booking;
  /** Whose perspective is this card rendered for? */
  perspective: 'customer' | 'provider';
  actions?: Action[];
};

export function BookingCard({ booking, perspective, actions = [] }: Props) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language?.startsWith('fr') ? frLocale : undefined;

  const totalCents = booking.services.reduce(
    (acc, s) => acc + (s.priceCents ?? 0),
    0,
  );
  const totalCurrency = booking.services.find((s) => s.currency)?.currency ?? null;
  const totalLabel =
    totalCents > 0 && totalCurrency
      ? formatPrice(totalCents, totalCurrency)
      : null;

  const totalDuration = booking.services.reduce(
    (acc, s) => acc + (s.durationMinutes ?? 0),
    0,
  );

  const when = booking.preferredDateTime
    ? format(parseISO(booking.preferredDateTime), 'EEEE d MMMM yyyy • HH:mm', {
        locale: dateLocale,
      })
    : null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'center' }}
        sx={{ mb: 1.5 }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {perspective === 'customer' ? (
            <>
              <Link
                component={RouterLink}
                to={`/providers/${booking.provider.id}`}
                underline="hover"
                sx={{ color: 'text.primary', fontWeight: 700, fontSize: '1.0625rem' }}
              >
                {booking.provider.businessName}
              </Link>
              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25, color: 'text.secondary' }}>
                <LocationOnIcon sx={{ fontSize: '0.95rem' }} />
                <Typography variant="body2">
                  {booking.provider.city}
                  {booking.provider.country ? `, ${booking.provider.country}` : ''}
                </Typography>
              </Stack>
            </>
          ) : (
            <>
              <Typography sx={{ fontWeight: 700, fontSize: '1.0625rem' }}>
                {booking.customer.name}
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 0.25, color: 'text.secondary' }}
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <EmailRoundedIcon sx={{ fontSize: '0.95rem' }} />
                  <Link
                    href={`mailto:${booking.customer.email}`}
                    underline="hover"
                    sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    {booking.customer.email}
                  </Link>
                </Stack>
                {booking.customer.phone && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <PhoneRoundedIcon sx={{ fontSize: '0.95rem' }} />
                    <Link
                      href={`tel:${booking.customer.phone.replace(/\s+/g, '')}`}
                      underline="hover"
                      sx={{ color: 'text.secondary', fontSize: '0.875rem' }}
                    >
                      {booking.customer.phone}
                    </Link>
                  </Stack>
                )}
              </Stack>
            </>
          )}
        </Box>
        <BookingStatusChip status={booking.status} />
      </Stack>

      {/* When */}
      {when && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <EventAvailableRoundedIcon sx={{ fontSize: '1.1rem', color: 'primary.main' }} />
          <Typography sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
            {when}
          </Typography>
        </Stack>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* Services */}
      <Stack spacing={0.75}>
        {booking.services.map((s) => (
          <Stack
            key={s.providerServiceId}
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
              {s.serviceName ?? '—'}
            </Typography>
            {s.durationMinutes != null && (
              <Typography variant="body2" color="text.secondary">
                {s.durationMinutes} min
              </Typography>
            )}
            {formatPrice(s.priceCents, s.currency) && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {formatPrice(s.priceCents, s.currency)}
              </Typography>
            )}
          </Stack>
        ))}
      </Stack>

      {/* Totals */}
      {(totalLabel || totalDuration > 0) && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {t('bookings.totalLabel')}
            </Typography>
            {totalDuration > 0 && (
              <Typography variant="body2" color="text.secondary">
                {totalDuration} min
              </Typography>
            )}
            {totalLabel && (
              <Typography sx={{ fontWeight: 800, color: 'primary.main' }}>
                {totalLabel}
              </Typography>
            )}
          </Stack>
        </>
      )}

      {/* Counter-proposal — show the original requested slot crossed out and
          the newly proposed one prominently. The actions buttons (Accept /
          Refuse for customer, just messaging for provider) live next to the
          card. */}
      {booking.status === 'COUNTER_PROPOSED' && booking.proposedDateTime && (
        <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {perspective === 'customer'
              ? t('bookings.counterProposedToYou')
              : t('bookings.counterProposedByYou')}
          </Typography>
          <Typography variant="body2">
            {t('bookings.counterProposedNewSlot')}{' '}
            <strong>
              {format(parseISO(booking.proposedDateTime),
                'EEEE d MMMM yyyy • HH:mm',
                { locale: dateLocale })}
            </strong>
          </Typography>
          {booking.proposedMessage && (
            <Typography
              variant="body2"
              sx={{ mt: 0.5, whiteSpace: 'pre-wrap', fontStyle: 'italic' }}
            >
              «{booking.proposedMessage}»
            </Typography>
          )}
        </Alert>
      )}

      {/* Status-specific notes */}
      {booking.status === 'REJECTED' && booking.rejectionReason && (
        <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
          {t('bookings.rejectionReasonLabel')}: {booking.rejectionReason}
        </Alert>
      )}
      {booking.status === 'CANCELLED' && booking.cancelledBy && (
        <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>
          {t(`bookings.cancelledBy.${booking.cancelledBy}`)}
          {/* Refund amount hidden for launch — re-enable with the rest of the
              refund flow:
          {booking.cancellationRefundCents != null &&
            booking.cancellationRefundCurrency &&
            ` ${t('bookings.refundIssued', {
              amount: formatPrice(
                booking.cancellationRefundCents,
                booking.cancellationRefundCurrency,
              ),
            })}`}
          */}
        </Alert>
      )}
      {booking.status === 'CANCELLATION_REQUESTED' && (
        <Alert severity="warning" sx={{ mt: 1.5, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {perspective === 'customer'
              ? t('bookings.cancellationAwaitingProvider')
              : t('bookings.cancellationAwaitingYou')}
          </Typography>
          {/* Refund-if-approved line hidden for launch — re-enable with the
              rest of the refund flow:
          {booking.cancellationRefundCents != null &&
            booking.cancellationRefundCurrency && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {t('bookings.refundIfApproved', {
                  amount: formatPrice(
                    booking.cancellationRefundCents,
                    booking.cancellationRefundCurrency,
                  ),
                })}
              </Typography>
            )}
          */}
          {booking.cancellationRequestMessage && (
            <Typography
              variant="body2"
              sx={{ mt: 0.5, whiteSpace: 'pre-wrap', fontStyle: 'italic' }}
            >
              «{booking.cancellationRequestMessage}»
            </Typography>
          )}
        </Alert>
      )}
      {booking.status === 'CONFIRMED' && booking.cancellationDeclineReason && (
        <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {t('bookings.cancellationDeclined')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
            {booking.cancellationDeclineReason}
          </Typography>
        </Alert>
      )}
      {booking.message && (
        <Box
          sx={{
            mt: 1.5,
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t('bookings.customerMessage')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }}>
            {booking.message}
          </Typography>
        </Box>
      )}

      {/* Actions */}
      {actions.length > 0 && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ mt: 2, justifyContent: 'flex-end' }}
        >
          {actions.map((a) => (
            <Button
              key={a.key}
              variant={a.variant ?? 'outlined'}
              color={a.color ?? 'primary'}
              onClick={a.onClick}
              disabled={a.disabled}
              size="small"
            >
              {a.label}
            </Button>
          ))}
        </Stack>
      )}
    </Paper>
  );
}
